import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import API from "../../../lib/api";
import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../ui/dialog";
import { Spinner, Pill, fmtDT } from "../hsUtils";
import { TONE_CLASSES } from "../../assets/assetUtils";
import { ArrowsLeftRight, DownloadSimple, Sparkle, X, Clock } from "@phosphor-icons/react";
import { err } from "./libraryUtils";

/* One request per page for "which of my copies have a newer version" — every banner on the page reads the same answer.
   Keyed by the signed-in user so an in-app account switch never shows the previous user's copies; banners re-read it when it is invalidated. */
let cache = { at: 0, promise: null, who: "" };
const listeners = new Set();
const whoami = () => { try { return (window.localStorage.getItem("access_token") || "").slice(-32); } catch { return ""; } };
export function loadUpdates(force = false) {
  const who = whoami();
  if (force || !cache.promise || cache.who !== who || Date.now() - cache.at > 30000) {
    cache = { at: Date.now(), who, promise: API.get("/hs/library/updates").then((r) => r.data).catch(() => ({ updates: [], count: 0 })) };
  }
  return cache.promise;
}
export const invalidateUpdates = () => { cache = { at: 0, promise: null, who: "" }; listeners.forEach((fn) => fn()); };
const onUpdatesChange = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };

const ROW_TONE = { added: "bg-emerald-50/70 dark:bg-emerald-950/20", removed: "bg-red-50/70 dark:bg-red-950/20", changed: "bg-amber-50/70 dark:bg-amber-950/20", same: "" };
const ROW_LABEL = { added: ["New", TONE_CLASSES.ok], removed: ["Removed", TONE_CLASSES.overdue], changed: ["Changed", TONE_CLASSES.soon] };

function Cell({ value, list, added = [], removed = [], side }) {
  if (list) {
    const items = value || [];
    if (!items.length) return <span className="text-muted-foreground">—</span>;
    return <ul className="space-y-0.5">{items.map((x, i) => { const hit = side === "new" ? added.includes(x) : removed.includes(x); return <li key={i} className={hit ? (side === "new" ? "text-emerald-700 dark:text-emerald-300 font-medium" : "text-red-700 dark:text-red-300 line-through") : ""}>• {x}</li>; })}</ul>;
  }
  return value ? <span className="whitespace-pre-wrap">{value}</span> : <span className="text-muted-foreground">—</span>;
}

/** Side-by-side: the library version a copy was taken from vs. the latest version — only changed fields unless "show everything". */
export function LibraryCompareDialog({ update, onClose, onTaken }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [all, setAll] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!update) return;
    setData(null);
    API.get(`/hs/library/${update.item.id}/changes`, { params: { from_version: update.copy.version } }).then((r) => setData(r.data)).catch((e) => { err(e, "Couldn't load the changes"); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [update]);
  const take = async () => {
    setBusy(true);
    try {
      const { data: res } = await API.post(`/hs/library/${update.item.id}/use`, { supersedes: update.copy.id });
      invalidateUpdates();
      toast.success(res.message);
      onTaken?.(res);
      onClose();
      navigate(res.url);
    } catch (e) { err(e, "Couldn't take the new version"); } finally { setBusy(false); }
  };
  const rows = (data?.diff || []).filter((r) => all || r.status !== "same");
  return (
    <Dialog open={!!update} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl sm:max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="lib-compare-dialog">
        {update && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><ArrowsLeftRight size={18} className="text-primary" /> What changed in “{update.item.title}”</DialogTitle>
              <DialogDescription>Your copy “{update.copy.title}” was taken from v{update.copy.version}{update.copy.adopted_at ? ` on ${fmtDT(update.copy.adopted_at)}` : ""}. The library is now on v{update.item.version}. Your own edits to your copy aren't shown here — this compares the library versions.</DialogDescription>
            </DialogHeader>
            {!data ? <Spinner /> : (
              <div className="space-y-3">
                {data.versions?.length > 0 && (
                  <ul className="rounded-md border border-border divide-y divide-border text-xs" data-testid="lib-compare-versions">
                    {data.versions.map((v) => <li key={v.version} className="px-3 py-1.5 flex flex-wrap gap-2"><b>v{v.version}</b><span className="text-muted-foreground">{fmtDT(v.published_at)}{v.published_by_name ? ` · ${v.published_by_name}` : ""}</span><span>{v.notes || <span className="text-muted-foreground italic">no release notes</span>}</span></li>)}
                  </ul>
                )}
                {data.snapshot_missing ? <p className="text-sm text-muted-foreground" data-testid="lib-compare-missing">No snapshot of v{update.copy.version} exists, so a field-by-field comparison isn't possible — preview the new version from the library instead.</p> : (
                  <>
                    <div className="flex items-center justify-between gap-2"><p className="text-xs text-muted-foreground" data-testid="lib-compare-count">{data.changed} field{data.changed === 1 ? "" : "s"} changed</p><label className="text-xs inline-flex items-center gap-1.5 cursor-pointer"><input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} data-testid="lib-compare-all" /> Show unchanged fields too</label></div>
                    {rows.length === 0 ? <p className="text-sm text-muted-foreground">Nothing changed in the content — probably a re-publish with the same wording.</p> : (
                      <div className="rounded-md border border-border overflow-hidden">
                        <table className="w-full text-xs table-fixed">
                          <thead className="bg-muted/60"><tr><th className="text-left px-3 py-2 w-[22%]">Field</th><th className="text-left px-3 py-2">v{update.copy.version} (your copy came from)</th><th className="text-left px-3 py-2">v{update.item.version} (latest)</th></tr></thead>
                          <tbody>{rows.map((r, i) => (
                            <tr key={i} className={`border-t border-border align-top ${ROW_TONE[r.status]}`} data-testid={`lib-compare-row-${i}`}>
                              <td className="px-3 py-2 font-medium">{r.label}{ROW_LABEL[r.status] && <div className="mt-1"><Pill tone={ROW_LABEL[r.status][1]}>{ROW_LABEL[r.status][0]}</Pill></div>}</td>
                              <td className="px-3 py-2"><Cell value={r.old} list={r.is_list} removed={r.removed} side="old" /></td>
                              <td className="px-3 py-2"><Cell value={r.new} list={r.is_list} added={r.added} side="new" /></td>
                            </tr>
                          ))}</tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            <DialogFooter className="gap-2 sm:gap-0">
              <p className="text-[11px] text-muted-foreground mr-auto self-center">Taking the new version adds a fresh copy — your current one is kept as it is.</p>
              <Button variant="outline" onClick={onClose} data-testid="lib-compare-close">Close</Button>
              <Button disabled={busy || !data} onClick={take} data-testid="lib-compare-take"><DownloadSimple size={14} className="mr-1" /> {busy ? "Copying…" : `Take v${update.item.version}`}</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Banner on a copied item (or a compact line on a card): "the library has a newer version" → Compare · Take · Dismiss. */
export function LibraryUpdateBanner({ copyId, compact = false, canEdit = true }) {
  const [update, setUpdate] = useState(null);
  const [open, setOpen] = useState(false);
  const [gone, setGone] = useState(false);
  useEffect(() => {
    let on = true;
    const read = () => loadUpdates().then((d) => on && setUpdate((d.updates || []).find((u) => u.copy.id === copyId) || null));
    read();
    const off = onUpdatesChange(read);
    return () => { on = false; off(); };
  }, [copyId]);
  if (!update || gone) return null;
  const dismiss = async () => {
    try { await API.post("/hs/library/updates/dismiss", { kind: update.copy.kind, copy_id: update.copy.id }); invalidateUpdates(); setGone(true); toast.success("Dismissed — you'll hear about the next version"); } catch (e) { err(e, "Couldn't dismiss"); }
  };
  const compare = <Button size="sm" variant={compact ? "ghost" : "outline"} className="h-7 text-xs" onClick={() => setOpen(true)} data-testid={`lib-update-compare-${copyId}`}><ArrowsLeftRight size={12} className="mr-1" /> Compare{compact ? "" : " & take the new version"}</Button>;
  return (
    <>
      {compact ? (
        <p className="mt-1 text-[11px] text-primary inline-flex items-center gap-1 flex-wrap" data-testid={`lib-update-${copyId}`}><Sparkle size={11} weight="fill" /> Library has v{update.item.version} (you copied v{update.copy.version}){update.copy.due_for_review && <span className="text-red-600 dark:text-red-400 font-medium" data-testid={`lib-update-due-${copyId}`}> · due for review — over a year old</span>} {canEdit && compare}</p>
      ) : (
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 flex flex-wrap items-center gap-3" data-testid={`lib-update-${copyId}`}>
          <Sparkle size={18} className="text-primary shrink-0" weight="fill" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">The template library has a newer version of this — v{update.item.version}{update.item.published_at ? `, published ${fmtDT(update.item.published_at)}` : ""}{update.copy.due_for_review && <Pill tone={TONE_CLASSES.overdue} testid={`lib-update-due-${copyId}`}><Clock size={11} className="mr-1" /> Due for review — your copy is over a year old</Pill>}</p>
            <p className="text-xs text-muted-foreground">{update.item.notes ? `What changed: ${update.item.notes}` : "No release notes were added."} Your copy came from v{update.copy.version} and stays exactly as it is unless you take the new one.</p>
          </div>
          {canEdit && <div className="flex gap-1.5 shrink-0">{compare}<Button size="sm" variant="ghost" className="h-7 text-xs text-muted-foreground" onClick={dismiss} data-testid={`lib-update-dismiss-${copyId}`}><X size={12} className="mr-1" /> Dismiss</Button></div>}
        </div>
      )}
      <LibraryCompareDialog update={open ? update : null} onClose={() => setOpen(false)} onTaken={() => setGone(true)} />
    </>
  );
}
